<template>
  <div class="color-search-result">
    <div v-if="isLoading && !searchResults.length" class="color-search-result__search-no-results">
      <circular-spinner-full :size="20" color="dark-blue" />
      Searching...
    </div>
    <div v-else-if="searchResults.length === 0" class="color-search-result__search-no-results">
      <div class="color-search-result__search-no-results-icon" v-html="noSearchResultsIcon"></div>
      No color found
    </div>
    <fs-scrollable v-else class="color-search-result__scrollable-block">
      <ul class="color-search-result__search-list">
        <color-row
          v-for="result in searchResults"
          :key="resultKey(result)"
          class="color-search-result__search-list-item"
          :color="displayColor(result)"
          size="large"
          @click="emit('select-color', result)"
        />
      </ul>
      <div
        v-if="!allColorsLoaded && !isLoading"
        v-observe-visibility="{
          callback: (visible: boolean) => visible && search(),
          intersection: { rootMargin: '50px' },
          throttle: 200,
        }"
        class="color-search-result__spinner-wrapper"
      >
        <circular-spinner-full key="spinner" :size="20" color="dark-blue" />
      </div>
    </fs-scrollable>
  </div>
</template>

<script setup lang="ts">
import type { ColorSearchResultDto } from '@/ApiClient';
import debounce from 'lodash/debounce';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ObserveVisibility as vObserveVisibility } from 'vue-observe-visibility';
import { type ColorSearchSource, useColors } from '@/store/colors';
import CircularSpinnerFull from '@components/circular-spinner-full.vue';
import ColorRow from '@components/color-row.vue';
import FsScrollable from '@components/fs-scrollable.vue';
import noSearchResultsIcon from '@icons/other2/no-search-results.svg';

const { searchQuery = '', source = {} } = defineProps<{
  searchQuery?: string;
  /** Which colours to look through. */
  source?: ColorSearchSource;
}>();

const emit = defineEmits<{
  'select-color': [result: ColorSearchResultDto];
}>();

// A result is either one of the brand's colours or an unadopted library colour; the row renders the
// values, while the pick keeps the whole result so the caller knows which it was.
const displayColor = (result: ColorSearchResultDto) => (result.color ?? result.libraryColor)!;

// Ids come from two different tables, so neither alone is unique across the list.
const resultKey = (result: ColorSearchResultDto) =>
  result.color ? `brand:${result.color.id}` : `library:${result.libraryColor!.id}`;

const pageSize = 16;
const searchResults = ref<ColorSearchResultDto[]>([]);
const allColorsLoaded = ref(false);
const isLoading = ref(false);

// A new query aborts whatever the previous one still has in flight. Otherwise a slow answer to an
// earlier query lands in the emptied list, and the new query's first request is sent as page 2.
let searchController = new AbortController();

async function search() {
  const controller = searchController;
  isLoading.value = true;
  try {
    const { colors } = await useColors().search(searchQuery, pageSize, searchResults.value.length, source, {
      signal: controller.signal,
    });
    searchResults.value = searchResults.value.concat(colors);
    allColorsLoaded.value = colors.length < pageSize;
    isLoading.value = false;
  } catch (e) {
    if (controller.signal.aborted) return;
    isLoading.value = false;
    throw e;
  }
}

const debouncedSearch = debounce(search, 300, { leading: true });

function restart() {
  searchController.abort();
  searchController = new AbortController();
  searchResults.value = [];
  allColorsLoaded.value = false;
  debouncedSearch();
}

watch([() => searchQuery, () => source], restart);
onMounted(debouncedSearch);
onBeforeUnmount(() => {
  debouncedSearch.cancel();
  searchController.abort();
});
</script>

<style scoped lang="scss">
@import '@styles/variables';

// Color Search Result ----------------------------------------------------------
.color-search-result {
  padding-top: 2.4rem;

  &__search-no-results {
    padding: 1.6rem 1rem 3.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;

    fill: rgba($c-dark, 0.5);
    color: rgba($c-dark, 0.5);
    font-size: 1.4rem;
    line-height: 1.6rem;
  }

  &__search-no-results-icon {
    width: 1.6rem;
    height: 1.6rem;
    fill: rgba($c-dark, 0.5);
  }

  &__scrollable-block {
    width: 100%;
    // The color-picker-iro has a fixed size in pixels, so we can't use only rems.
    height: calc(15.6rem + 222px);
  }

  &__search-list {
    padding: 0;
    margin: 0;
  }

  &__search-list-item {
    border-radius: 0.5rem;
    cursor: pointer;

    &:hover {
      background-color: rgba($c-dark, 0.1);
    }
  }

  &__spinner-wrapper {
    height: 3rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
}
</style>
